/* eslint-disable @typescript-eslint/no-deprecated -- existing BdsButtonGroup/BdsRollupButton usage; deprecation migration to BTOverflow is a separate task */
import { message } from "antd";
import { type SortOrder } from "antd/lib/table/interface";
import update from "immutability-helper";
import { type IExternalServiceInfo } from "legacyComponents/FileUploadContainer.types";
import {
    type AttachedFiles,
    type AttachedFilesRequest,
    type BTFileSystem,
    type IHasAttachedFiles,
} from "legacyComponents/FileUploadContainer.types";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useRouteMatch } from "react-router";
import { Screen } from "styles/antTheme/Variables";
import { useMediaQuery } from "usehooks-ts";
import { type IBaseSlide } from "yet-another-react-lightbox";

import { BTText } from "@btnet/core-components";
import { cn } from "@btnet/utilities-core";
import { mobile } from "@btnet/utilities-webview";

import { BdsButtonGroup, BdsRollupButton } from "@buildertrend/components";

// eslint-disable-next-line no-restricted-imports -- Draw only renders in an authenticated builder/media session; reading enableNutrientPhotoDraw here (defaults off when no builder context) avoids threading the flag through AttachmentContainer's ~55 callers. ado-277127
import { BuilderInfoContext } from "helpers/globalContext/BuilderInfoContext";
import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLocalStorage } from "types/btStorage";
import { BTLoginTypes, MediaType } from "types/enum";
import { type BTViewingPermissions } from "types/filePermissions";

import { type ITrackingProp, track } from "utilities/analytics/analytics";
import { showAPIErrorMessage } from "utilities/apiHandler";
import {
    generateExternalAttachmentFromLink,
    triggerBrowserDownload,
} from "utilities/document/fileDownload.utils";
import { useActionBeingPerformed } from "utilities/form/form";
import { getJobPickerState } from "utilities/jobPicker/jobPicker";
import { lazyLoadComponent } from "utilities/LazyLoad.utility";
import { routes } from "utilities/routes";

import { btDeleteConfirmAsync } from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { BTLightbox } from "commonComponents/btWrappers/BTLightbox/BTLightbox";
import {
    btFileSystemToSlide,
    canViewBTFileSystemInLightbox,
} from "commonComponents/btWrappers/BTLightbox/BTLightbox.utility";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { usePanelAwareNavigate } from "commonComponents/btWrappers/BTSplitView/usePanelAwareNavigate";
import {
    AnnotationsHandler,
    type IAnnotationsHandler,
} from "commonComponents/entity/media/Annotations/Annotations.api.handler";
import { type AnnotationsUrlResponse } from "commonComponents/entity/media/Annotations/Annotations.api.types";
import { isAttachedFilesBulkDownloadSupported } from "commonComponents/entity/media/AttachmentContainer/AttachmentContainer.utils";
import {
    attachBrowsedBTFilesToExistingEntity,
    attachWebviewFilesToExistingEntity,
    buildBTFileSystemsFromWebviewResult,
    buildWebviewPickerOptions,
    shapeBrowsedBTFilesForAttachment,
} from "commonComponents/entity/media/AttachmentContainer/AttachmentContainer.webview.utils";
import { BrowseBTFiles } from "commonComponents/entity/media/BrowseBTFiles/BrowseBTFiles";
import BTFileUpload from "commonComponents/entity/media/BTFileUpload/BTFileUpload";
import { BTFileUploadMode } from "commonComponents/entity/media/BTFileUpload/BTFileUpload.api.types";
import { MediaGrid } from "commonComponents/entity/media/common/MediaGrid/MediaGrid";
import { MediaTable } from "commonComponents/entity/media/common/MediaTable/MediaTable";
import { FileViewToggle } from "commonComponents/entity/media/FileViewToggle/FileViewToggle";
import { RouteMediaPropertiesFromAttachedFiles } from "commonComponents/entity/media/MediaProperties/RouteMediaProperties";
import { ViewAllAttachments } from "commonComponents/entity/media/ViewAllAttachments/ViewAllAttachments";
import { type ViewAllAttachmentsActions } from "commonComponents/entity/media/ViewAllAttachments/ViewAllAttachments.types";
import {
    type IZohoHandler,
    ZohoHandler,
} from "commonComponents/entity/media/Zoho/Zoho.api.handler";
import { RouteZoho } from "commonComponents/entity/media/Zoho/ZohoRoute";
import { RouteRelative } from "commonComponents/utilities/RouteRelative/RouteRelative";

import {
    DocumentHandler,
    type IDocumentHandler,
} from "entity/document/Document/Document.api.handler";
import { type SetMainPhotoRequest } from "entity/document/Document/Document.api.types";
import {
    BulkDownloadHandler,
    type IBulkDownloadHandler,
} from "entity/media/common/BulkDownload.api.handler";
import {
    canMultiSelectFiles,
    convertFilesToAttachedFilesRequest,
    getDocumentInstanceTypeString,
    sortMediaByMainFileAndDateAttached,
} from "entity/media/common/media.utils";
import {
    type IMediaListHandler,
    MediaListHandler,
} from "entity/media/common/MediaList.api.handler";
import {
    AnnotationType,
    type AttachedMedia,
    type DocumentInstanceUpdateType,
    type IDownloadZipOfAttachedFilesForEntityRequest,
    type IZohoSessionInfo,
    type SelectedMediaListViews,
} from "entity/media/common/mediaTypes";
import { RoutePhotoDraw } from "entity/media/common/RoutePhotoDraw";
import { type BreakMediaLinksResponse } from "entity/media/MediaReplace/MediaReplace.api.types";
import { RouteMediaReplace } from "entity/media/MediaReplace/RouteMediaReplace";
import {
    type IVideoListHandler,
    VideoListHandler,
} from "entity/video/VideoList/VideoList.api.handler";

import "./AttachmentContainer.less";

const DocumentCreate = lazyLoadComponent(
    () => import("entity/document/DocumentCreate/DocumentCreate"),
);

interface IAttachmentContainerProps {
    entity: IHasAttachedFiles;
    /** Overload for passing post approval files */
    attachedFiles?: AttachedFiles;
    /**
     * Whether the component is readonly or not.
     * @default false
     */
    isReadOnly?: boolean;
    /**
     * Whether the component is being viewed from an external page.
     * @default false
     */
    isOnExternalPage?: boolean;
    /** Return true to suppress the default click handling (lightbox/route/download). */
    onFileClick?: (file: BTFileSystem) => boolean;
    externalServiceInfo?: IExternalServiceInfo;
    /**
     * Key name that the files object will be written to on change.
     * Note: this only works for an entity id of 0, otherwise changes
     * are not written back to the parent.
     * @example ``` idForFiles="attachedFiles" ```
     */
    idForFiles?: string;
    /**
     * Pass setFieldValue or a corresponding change function.
     * Note: this only works for an entity id of 0, otherwise changes
     * are not written back to the parent.
     */
    onChangeForNewEntity?: (fieldName: string, value: AttachedFilesRequest) => void;
    /**
     * A callback specifically for interacting with the full files array.
     * Used to update files in the parent component when a file is added or removed.
     */
    onAllFileChange?: (files: BTFileSystem[]) => void;
    onViewingPermissionChange?: (
        id: number,
        key: "showOwner" | "showSubs",
        shouldShow: boolean,
    ) => void;

    bulkDownloadHandler?: IBulkDownloadHandler;
    videoListHandler?: IVideoListHandler;
    documentHandler?: IDocumentHandler;
    mediaListHandler?: IMediaListHandler;
    zohoHandler?: IZohoHandler;
    annotationsHandler?: IAnnotationsHandler;
    /**
     * This is a boolean prop that should be changed to true when you want to programmatically make the "Attach Files" modal appear.
     * This is the same modal that appears when clicking "Add" within the component, but should NOT be implemented unless you have a
     * use case outside of the normal "Add" button.
     * @example ``` isAttachFilesOpen={this.state.isAttachFilesOpen} ```
     */
    isAttachFilesOpen?: boolean;
    /**
     * This callback will fire when the "Attach Files" modal is closed.
     * @example ```onAttachFilesClose={() => this.setState({ isAttachFilesOpen: false })}```
     */
    onAttachFilesClose?: () => void;
    /**
     * This callback will fire when the "Attach Files" modal is open.
     * @example ```onAttachFilesOpen={() => this.setState({ isAttachFilesOpen: true })}```
     */
    onAttachFilesOpen?: () => void;
    /** Externally control the "Create New Doc" modal. */
    isCreateFilesOpen?: boolean;
    /** Fires when the "Create New Doc" modal closes. */
    onCreateFilesClose?: () => void;
    /** Fires when the "Create New Doc" modal opens. */
    onCreateFilesOpen?: () => void;
    /** Externally control the "View All" modal. */
    isViewAllOpen?: boolean;
    /** Fires when the "View All" modal closes. */
    onViewAllClose?: () => void;
    showTitle?: boolean;
    /**
     * Array of file IDs that should not be deletable (e.g., base files used for OCR).
     * The "Move to trash" button will be disabled if any of these files are selected.
     * @example ``` nonDeletableFileIds={[bill.baseFileId]} ```
     */
    nonDeletableFileIds?: number[];
    /**
     * Tooltip shown on the disabled "Move to trash" / "Remove selected" button when
     * non-deletable files are selected or are the only files present.
     */
    nonDeletableTooltip?: string;
    /**
     * You should test whether or not sticky headers on the media table cause problems when you use this component.
     * Sticky headers on the media table can be disabled if context attachments appears in doesn't support it (not responsive)
     * or if it doesn't make sense to be sticky (print pages).
     * @default true
     */
    hasStickyTableHeader?: boolean;
    /**
     * When true, hides the "Edit online" context menu item and the "Create new doc" button.
     * Use this for entities where online document editing is not supported (e.g. Submittals).
     * @default false
     */
    hideCreateAndEditDocOnline?: boolean;
    /**
     * When true, enables an inline "Rename" affordance in the table view's context menu
     * for files the user can edit (builder + `permissions.canEdit`).
     * @default false
     */
    enableRename?: boolean;
    /**
     * When true, removes the vertical padding around the container's root element.
     * @default false
     */
    removeVerticalPadding?: boolean;
    /**
     * Called whenever the user changes the sort column or direction in the table view.
     * Receives the column field and the new sort direction, or `undefined`
     * for both when sorting is cleared.
     */
    onSortChange?: (columnField: string | undefined, order: SortOrder | undefined) => void;
    /**
     * Use when attachments are loaded by view order on the server
     * @default false
     */
    viewByOrder?: boolean;

    highlightedFileId?: number;
    /**
     * Device-local key for background uploads on a NEW entity (id === 0). Mint it
     * once for the create-form session and pass the same value here and to the
     * eventual {@link resolveAttachmentCorrelation}/{@link abandonAttachmentCorrelation}
     * call in the entity's save/cancel handler, so RN can finalize or drop the
     * uploads it queued under this key. Ignored for existing entities.
     */
    correlationId?: string;
    /**
     * Whether this entity opts into RN backgrounding attachment uploads inside the
     * mobile webview. Per-entity capability flag (default off) surfaced to the
     * native picker via the contract's `allowBackgroundUploads`. No-op on web.
     * Applies to existing entities too, so it is independent of {@link correlationId}
     * (which is new-entity only).
     */
    allowWebviewBackgroundUploads?: boolean;

    onFileOrderChanged?: (hasFileOrderChanged: boolean) => void;
}

interface IFileMetadata {
    docInstanceId: number;
    isOnTempFile?: boolean;
    annotationType?: AnnotationType;
    attachedMedia?: AttachedMedia[];
}

const defaultBulkDownloadHandler = new BulkDownloadHandler();
const defaultVideoListHandler = new VideoListHandler();
const defaultDocumentHandler = new DocumentHandler();
const defaultMediaListHandler = new MediaListHandler();
const defaultZohoHandler = new ZohoHandler();
const defaultAnnotationsHandler = new AnnotationsHandler();
const defaultNonDeletableFileIds: number[] = [];

export const AttachmentContainer = track({ component: "AttachmentContainer" })(({
    entity,
    attachedFiles,
    isReadOnly = false,
    isOnExternalPage = false,
    onFileClick,
    externalServiceInfo,
    idForFiles,
    onChangeForNewEntity,
    onAllFileChange,
    isAttachFilesOpen,
    onAttachFilesOpen,
    onAttachFilesClose,
    isCreateFilesOpen,
    onCreateFilesClose,
    onCreateFilesOpen,
    isViewAllOpen,
    onViewAllClose,
    onViewingPermissionChange,
    bulkDownloadHandler = defaultBulkDownloadHandler,
    videoListHandler = defaultVideoListHandler,
    documentHandler = defaultDocumentHandler,
    mediaListHandler = defaultMediaListHandler,
    zohoHandler = defaultZohoHandler,
    annotationsHandler = defaultAnnotationsHandler,
    showTitle = false,
    nonDeletableFileIds = defaultNonDeletableFileIds,
    nonDeletableTooltip,
    hasStickyTableHeader = true,
    hideCreateAndEditDocOnline = false,
    enableRename = false,
    removeVerticalPadding = false,
    onSortChange,
    onFileOrderChanged,
    viewByOrder = false,
    highlightedFileId,
    correlationId,
    allowWebviewBackgroundUploads,
}: IAttachmentContainerProps & ITrackingProp) => {
    const isSmallScreen = useMediaQuery(`(max-width: ${Screen.sm}px)`);
    const hideTableView = mobile.isWebview && isSmallScreen;
    const [actionBeingPerformed, setActionBeingPerformed] =
        useActionBeingPerformed<ViewAllAttachmentsActions>();
    const [files, setFiles] = useState([
        ...(entity.attachedFiles?.files ?? attachedFiles?.files ?? []),
    ]);
    const [selectedFileIds, setSelectedFileIds] = useState<number[]>([]);
    const [showUploadFileModal, setShowUploadFileModal] = useState<boolean>(
        isAttachFilesOpen ?? false,
    );
    const [showCreateNewDocModal, setShowCreateNewDocModal] = useState<boolean>(
        isCreateFilesOpen ?? false,
    );
    const [showViewAllModal, setShowViewAllModal] = useState<boolean>(isViewAllOpen ?? false);
    const [showBrowseBTFilesModal, setShowBrowseBTFilesModal] = useState<boolean>(false);
    const [selectedLightboxFileId, setSelectedLightboxFileId] = useState<number | null>(null);
    const [isShowingMore, setIsShowingMore] = useState<boolean>(false);
    const [view, setView] = useState<SelectedMediaListViews>(
        BTLocalStorage.get("bt-string-selectedAttachmentsContainerView"),
    );
    const [currentFile, setCurrentFile] = useState<IFileMetadata | null>(null);
    const [zohoSessionInfo, setZohoSessionInfo] = useState<IZohoSessionInfo | undefined>(undefined);
    const [buttonGroupKey, setButtonGroupKey] = useState(0);
    const [renamingId, setRenamingId] = useState<number | null>(null);

    const filesAreSelected = selectedFileIds.length > 0;

    const history = useHistory();
    const match = useRouteMatch();
    const userInfo = useContext(UserInfoContext);
    const builderInfo = useContext(BuilderInfoContext);
    // Defaults to off when there is no builder context, so the Draw action falls back to the
    // legacy flow and never throws when rendered outside an authenticated builder session.
    const enableNutrientPhotoDraw = builderInfo?.flags?.enableNutrientPhotoDraw ?? false;
    const attachmentRoute = `/Attachment/${entity.name}/${entity.id}`;
    // Inside a split-view panel, carry the panel's query onto document-modal nav +
    // cleanup redirects so the panel stays mounted. No-op outside a panel.
    const { querySuffix } = usePanelAwareNavigate();
    const baseRoute = match.url + querySuffix;
    const isOnNewEntity = entity.id === 0;

    const useRNFilePicker =
        mobile.isWebview && typeof mobile.openFilePickerAttachments === "function";

    const nextViewingOrder = viewByOrder
        ? files.reduce((max, f) => Math.max(max, f.viewingOrder ?? -1), -1) + 1
        : undefined;

    // This is a somewhat hacky solution to get the BdsButtonGroup to rerender and expand after a window resize previously caused it to collapse
    // BdsButtonGroup's ResizeObserver can't detect re-expansion after rollup because display:none on rolled-up
    // buttons collapses the button col's flex-basis. Remounting after resize forces a fresh layout measurement.
    // Only an issue when one or more files are selected because of the selected count
    useEffect(() => {
        if (!filesAreSelected) return;
        let timeoutId: ReturnType<typeof setTimeout>;
        function handleAttachmentContainerResize() {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => setButtonGroupKey((k) => k + 1), 250);
        }
        window.addEventListener("resize", handleAttachmentContainerResize);
        return () => {
            window.removeEventListener("resize", handleAttachmentContainerResize);
            clearTimeout(timeoutId);
        };
    }, [filesAreSelected]);

    useEffect(() => {
        // Attached files will contain the up to date file list since it will contain files added or removed since the parent entity was saved
        const newAttachedFiles = attachedFiles || entity.attachedFiles;

        if (newAttachedFiles) {
            let updatedFiles = [...newAttachedFiles.files];
            if (!viewByOrder) {
                updatedFiles = updatedFiles.sort(sortMediaByMainFileAndDateAttached);
            }
            setFiles(updatedFiles);
        }
    }, [attachedFiles, entity.attachedFiles, viewByOrder]);

    // Intentionally use empty dependency array because we only want to set the initial files on mount
    // This handles pre-attached files and avoids infinite looping due to attahcedFiles changing on every update
    useEffect(() => {
        const newAttachedFiles = attachedFiles || entity.attachedFiles;
        if (isOnNewEntity && onChangeForNewEntity && newAttachedFiles?.files.length > 0) {
            onChangeForNewEntity(
                idForFiles ?? "attachedFiles",
                convertFilesToAttachedFilesRequest(newAttachedFiles.files),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isAttachFilesOpen !== undefined) {
            setShowUploadFileModal(isAttachFilesOpen);
        }
    }, [isAttachFilesOpen]);

    useEffect(() => {
        if (isCreateFilesOpen !== undefined) {
            setShowCreateNewDocModal(isCreateFilesOpen);
        }
    }, [isCreateFilesOpen]);

    useEffect(() => {
        if (isViewAllOpen !== undefined) {
            setShowViewAllModal(isViewAllOpen);
        }
    }, [isViewAllOpen]);

    const resetSelectedFiles = useCallback(() => {
        setSelectedFileIds((currentSelectedIds) => {
            return currentSelectedIds.length > 0 ? [] : currentSelectedIds;
        });
    }, []);

    // Reset selected files when entity is saved (transitions from new to saved)
    useEffect(() => {
        if (!isOnNewEntity) {
            resetSelectedFiles();
        }
    }, [isOnNewEntity, resetSelectedFiles]);

    // Reset selected files when multi-select capability changes to false
    useEffect(() => {
        const canSelect = canMultiSelectFiles(files, isOnExternalPage, isReadOnly);
        if (!canSelect && selectedFileIds.length > 0) {
            resetSelectedFiles();
        }
    }, [files, isOnExternalPage, isReadOnly, selectedFileIds, resetSelectedFiles]);

    // Update selectedFileIds when files change (e.g. when a single file is deleted via the context menu)
    useEffect(() => {
        if (selectedFileIds.length > 0) {
            const existingFileIds = files.map((file) => file.id);
            const updatedSelectedFileIds = selectedFileIds.filter((id) =>
                existingFileIds.includes(id),
            );
            if (updatedSelectedFileIds.length !== selectedFileIds.length) {
                setSelectedFileIds(updatedSelectedFileIds);
            }
        }
    }, [files, selectedFileIds]);

    const handleChangeView = (value: SelectedMediaListViews) => {
        setView(value);
        BTLocalStorage.set("bt-string-selectedAttachmentsContainerView", value);
    };

    const handleFilesChanged = useCallback(
        (updatedFiles: BTFileSystem[]) => {
            if (!viewByOrder) {
                const sortedFiles = [...updatedFiles].sort(sortMediaByMainFileAndDateAttached);
                setFiles(sortedFiles);
                onAllFileChange?.(sortedFiles);
            } else {
                setFiles(updatedFiles);
                onAllFileChange?.(updatedFiles);
            }

            if (entity.id === 0 && onChangeForNewEntity) {
                const attachedFiles = convertFilesToAttachedFilesRequest(updatedFiles);
                onChangeForNewEntity(idForFiles ?? "attachedFiles", attachedFiles);
            }
        },
        [viewByOrder, onAllFileChange, entity.id, onChangeForNewEntity, idForFiles],
    );

    const getUpdatedAnnotationUrl = async () => {
        if (!currentFile?.annotationType) {
            return;
        }
        try {
            const urlResponse = await annotationsHandler.getAnnotationUrl(
                currentFile.docInstanceId,
                currentFile.annotationType,
                entity.jobId,
            );
            handleAnnotationAdded(currentFile.docInstanceId, urlResponse);
        } catch (e) {
            showAPIErrorMessage(e);
        }
    };

    const handleReplaceBeforeClose = async () => {
        setCurrentFile(null);
        await setActionBeingPerformed({
            actionBeingPerformed: undefined,
            callback: (): Promise<void> => {
                history.replace(baseRoute);
                return Promise.resolve();
            },
        });
    };

    const handleCloseDrawModal = async () => {
        await setActionBeingPerformed({
            actionBeingPerformed: undefined,
            callback: async () => {
                await getUpdatedAnnotationUrl();
                setCurrentFile(null);
            },
        });

        if (history.length > 0) {
            history.goBack();
        } else {
            history.replace(baseRoute);
        }
    };

    const handleAnnotationAdded = (
        docIntanceId: number,
        annotationData: AnnotationsUrlResponse,
    ) => {
        const updatedFiles = update(files, {
            $apply: (files: BTFileSystem[]) =>
                files.map((file) => {
                    if (file.id !== docIntanceId) {
                        return file;
                    }
                    return update(file, {
                        annotatedDocPath: {
                            $set: annotationData.annotatedDocPathUrl,
                        },
                        hasAnnotations: {
                            $set: annotationData.hasAnnotations,
                        },
                        annotationGroupId: {
                            $set: annotationData.annotationGroupId ?? null,
                        },
                        previewAnnotatedDocPath: {
                            $set: annotationData.previewAnnotatedDocPathUrl ?? "",
                        },
                        thumbnail: {
                            $set: annotationData.thumbnailUrl,
                        },
                    });
                }),
        });
        handleFilesChanged(updatedFiles);
    };

    const handleBreakDocLink = (newDocInstanceId: number, prevDocInstanceId: number) => {
        const updatedFiles = update(files, {
            $apply: (files: BTFileSystem[]) =>
                files.map((file) => {
                    if (file.id !== prevDocInstanceId) {
                        return file;
                    }

                    return update(file, {
                        id: {
                            $set: newDocInstanceId,
                        },
                    });
                }),
        });

        setCurrentFile({ ...currentFile, docInstanceId: newDocInstanceId } as IFileMetadata);
        handleFilesChanged(updatedFiles);
    };

    const getZohoDocBrowserInfo = useCallback(
        async (docInstanceId: number) => {
            try {
                const docBrowserInfo = await zohoHandler.viewDocumentInBrowser(docInstanceId);
                if (!docBrowserInfo) {
                    return;
                }

                const sessionInfo: IZohoSessionInfo = {
                    documentUrl: docBrowserInfo.urlForDoc,
                    documentDeleteUrl: docBrowserInfo.zohoDocumentDeleteUrl,
                    sessionDeleteUrl: docBrowserInfo.zohoSessionDeleteUrl,
                };
                setZohoSessionInfo(sessionInfo);
                return docBrowserInfo;
            } catch (e) {
                showAPIErrorMessage(e);
                return;
            }
        },
        [zohoHandler],
    );

    const getAttachmentRoute = useCallback(
        (route: string, _documentInstanceId: number) => {
            return match.url + attachmentRoute + route + querySuffix;
        },
        [attachmentRoute, match.url, querySuffix],
    );

    const handleZohoBeforeClose = async () => {
        await setActionBeingPerformed({
            actionBeingPerformed: undefined,
            callback: async () => {
                if (zohoSessionInfo?.documentDeleteUrl) {
                    const deleteZohoDocData = [zohoSessionInfo.documentDeleteUrl];
                    await zohoHandler?.deleteZohoDocument(deleteZohoDocData);
                }
                setZohoSessionInfo(undefined);
            },
        });

        if (history.length > 0) {
            history.goBack();
        } else {
            history.replace(baseRoute);
        }
    };

    const openFileInZoho = useCallback(
        async (docInstanceId: number) => {
            await getZohoDocBrowserInfo(docInstanceId);
            const editOnlineLink = routes.document.getEditOnlineLink(docInstanceId);
            history.push(getAttachmentRoute(editOnlineLink, docInstanceId));
        },
        [getAttachmentRoute, getZohoDocBrowserInfo, history],
    );

    const handleReplace = async (
        response: BreakMediaLinksResponse,
        _updateType: DocumentInstanceUpdateType,
    ) => {
        if (!currentFile || !userInfo) {
            history.replace(baseRoute);
            return;
        }

        handleBreakDocLink(response.documentInstanceId, currentFile.docInstanceId);
        if (actionBeingPerformed === "draw") {
            const drawLink = enableNutrientPhotoDraw
                ? routes.media.getMediaPropertiesLink(
                      response.documentInstanceId,
                      MediaType.Photo,
                      entity.jobId,
                  )
                : routes.photo.getPhotoDrawLink(
                      response.documentInstanceId,
                      entity.builderId,
                      userInfo.loginType,
                      false,
                      false,
                  );
            history.push(getAttachmentRoute(drawLink, response.documentInstanceId));
        } else if (actionBeingPerformed === "editOnline") {
            await openFileInZoho(response.documentInstanceId);
        } else {
            history.replace(baseRoute);
        }
    };

    const handleSlideChange = (_: number, slide: IBaseSlide) => {
        if (slide.metadata?.mediaId === undefined) {
            return;
        }
        setSelectedLightboxFileId(slide.metadata.mediaId);
    };

    const handleReorderFiles = useCallback(
        (reordered: BTFileSystem[]) => {
            handleFilesChanged(reordered);
            onFileOrderChanged?.(true);
        },
        [handleFilesChanged, onFileOrderChanged],
    );

    const handleFilesAdded = (fileList: BTFileSystem[]) => {
        const allFiles = viewByOrder ? [...files, ...fileList] : [...fileList, ...files];
        handleFilesChanged(allFiles);
    };

    const handleBulkDownload = async (e: React.SyntheticEvent) => {
        e.stopPropagation();
        await setActionBeingPerformed({
            actionBeingPerformed: "download",
            callback: async () => {
                const data: IDownloadZipOfAttachedFilesForEntityRequest = {
                    documentInstanceType: entity.documentInstanceType,
                    entityId: entity.id,
                    jobId: entity.jobId,
                };

                try {
                    const response =
                        await bulkDownloadHandler.downloadZipOfAttachedDocsForEntity(data);
                    triggerBrowserDownload(response.blobData, response.fileName);
                } catch (e) {
                    showAPIErrorMessage(e);
                }
            },
        });
    };

    const handleClickAdd = async () => {
        // When in a webview, use a native file picker to select files and upload them as temp files
        if (useRNFilePicker && mobile.isWebview && mobile.openFilePickerAttachments) {
            onAttachFilesOpen?.();
            try {
                const options = buildWebviewPickerOptions(
                    entity,
                    correlationId,
                    allowWebviewBackgroundUploads,
                );
                const result = await mobile.openFilePickerAttachments(options);
                if (entity.id === 0) {
                    handleFilesAdded(buildBTFileSystemsFromWebviewResult(result));
                } else {
                    handleFilesAdded(
                        await attachWebviewFilesToExistingEntity(
                            entity,
                            result,
                            undefined,
                            nextViewingOrder,
                        ),
                    );
                }
            } catch (e) {
                showAPIErrorMessage(e);
            } finally {
                onAttachFilesClose?.();
            }
            return;
        }
        onAttachFilesOpen?.();
        setShowUploadFileModal(true);
    };

    const handleClickBrowseBTFiles = () => {
        setShowBrowseBTFilesModal(true);
    };

    const handleCloseBrowseBTFiles = () => {
        setShowBrowseBTFilesModal(false);
    };

    const handleBrowseBTFilesSelected = async (selectedFiles: BTFileSystem[]) => {
        try {
            if (entity.id === 0) {
                handleFilesAdded(shapeBrowsedBTFilesForAttachment(selectedFiles, true));
            } else {
                handleFilesAdded(
                    await attachBrowsedBTFilesToExistingEntity(
                        entity,
                        selectedFiles,
                        undefined,
                        nextViewingOrder,
                    ),
                );
            }
        } catch (e) {
            showAPIErrorMessage(e);
        } finally {
            setShowBrowseBTFilesModal(false);
        }
    };

    const handleCloseUploadModal = () => {
        onAttachFilesClose?.();
        setShowUploadFileModal(false);
    };

    const handleOpenCreateNewDocModal = () => {
        onCreateFilesOpen?.();
        setShowCreateNewDocModal(true);
    };

    const handleCloseCreateNewDocModal = () => {
        onCreateFilesClose?.();
        setShowCreateNewDocModal(false);
    };

    const handleCloseViewAllModal = () => {
        onViewAllClose?.();
        setShowViewAllModal(false);
    };

    const handleOpenViewAllFromAdd = () => {
        onAttachFilesOpen?.();
        setShowUploadFileModal(true);
    };

    const downloadVideos = async (videoIds: number[], jobId: number) => {
        try {
            await setActionBeingPerformed({
                actionBeingPerformed: "download",
                callback: async () => {
                    const response = await videoListHandler.getVideoDownloadLinks(videoIds, jobId);
                    if (response.linksList?.length === 0) {
                        void message.info("This content is not yet available");
                        return;
                    }

                    await Promise.all(
                        response.linksList.map((linkUrl, i) =>
                            generateExternalAttachmentFromLink(linkUrl, i),
                        ),
                    );
                },
            });
        } catch (e) {
            showAPIErrorMessage(e);
        }
    };

    const handleVideoDownload = async (file: BTFileSystem) => {
        if (file.videoId && file.jobsiteId) {
            const videoIds = [file.videoId];
            await downloadVideos(videoIds, file.jobsiteId);
        }
    };

    const handleStartRename = (file: BTFileSystem) => {
        setRenamingId(file.id);
    };

    const handleCancelRename = () => {
        setRenamingId(null);
    };

    const handleSubmitRename = async (file: BTFileSystem, newTitle: string) => {
        const trimmed = newTitle.trim();
        if (!trimmed || trimmed === file.title || !files.some((f) => f.id === file.id)) {
            setRenamingId(null);
            return;
        }
        try {
            await setActionBeingPerformed({
                actionBeingPerformed: "rename",
                callback: async () => {
                    await documentHandler.renameFile(file.id, trimmed);
                    handleFilesChanged(
                        files.map((f) => (f.id === file.id ? { ...f, title: trimmed } : f)),
                    );
                    void message.success("File renamed successfully");
                    setRenamingId(null);
                },
            });
        } catch (e) {
            setRenamingId(null);
            showAPIErrorMessage(e, "There was an error renaming this file, please try again");
        }
    };

    const handleFileDelete = async (file: BTFileSystem) => {
        const isTempFile = file.tempId !== undefined;
        let title = `Move attachment to trash`;
        let content = `This file will only be removed from this ${getDocumentInstanceTypeString(
            entity.documentInstanceType,
        )}. If it is attached elsewhere, it will remain attached there.`;
        let okText = "Move to trash";

        if (isTempFile || isOnNewEntity) {
            title = `Remove attachment from this ${getDocumentInstanceTypeString(
                entity.documentInstanceType,
            )}`;
            content = `Are you sure you want to remove this attachment from this ${getDocumentInstanceTypeString(
                entity.documentInstanceType,
            )}?`;
            okText = "Remove";
        }

        const deleteConfirmAsync = await btDeleteConfirmAsync({
            title: title,
            content: content,
            okText: okText,
            okButtonProps: { "data-testid": "confirmRemoveAttachmentButton" },
            cancelText: "Cancel",
            cancelButtonProps: { "data-testid": "cancelRemoveAttachmentButton" },
        });

        if (deleteConfirmAsync === "cancel") {
            return;
        }

        const remainingFiles = files.filter((f) => f.id !== file.id);
        if (isTempFile || isOnNewEntity) {
            handleFilesChanged(remainingFiles);
            await message.success("File deleted successfully");
            return;
        }

        try {
            if (isOnExternalPage) {
                await setActionBeingPerformed({
                    actionBeingPerformed: "delete",
                    callback: async () => {
                        await documentHandler.externalDelete(
                            externalServiceInfo?.shareToken ?? "",
                            file.id,
                        );
                    },
                });
            } else {
                await setActionBeingPerformed({
                    actionBeingPerformed: "delete",
                    callback: async () => {
                        await documentHandler.delete(
                            entity.documentInstanceType,
                            [file.id],
                            entity.builderId,
                            entity.id,
                            entity.jobId,
                        );
                    },
                });
            }

            handleFilesChanged(remainingFiles);

            await message.success("File moved to trash successfully");
        } catch (e) {
            showAPIErrorMessage(e, "There was an error deleting this file, please try again");
        }
    };

    const updateViewingPermissions = (
        id: number,
        key: "showOwner" | "showSubs",
        shouldShow: boolean,
    ) => {
        const newAttachedFiles = update(files, {
            $apply: (files: BTFileSystem[]) =>
                files.map((file) => {
                    if (file.id !== id) {
                        return file;
                    }

                    return update(file, {
                        viewingPermissions: {
                            [key]: {
                                $set: shouldShow,
                            },
                        },
                    });
                }),
        });

        handleFilesChanged(newAttachedFiles);
    };

    const handleViewPermissionsUpdate = async (file: BTFileSystem, loginType: BTLoginTypes) => {
        if (file.viewingPermissions === undefined) {
            return;
        }

        let isVisible = false;
        if (loginType === BTLoginTypes.CONTACT) {
            isVisible = !file.viewingPermissions.showOwner;
            onViewingPermissionChange?.(file.id, "showOwner", isVisible);
        } else if (loginType === BTLoginTypes.SUBS) {
            isVisible = !file.viewingPermissions.showSubs;
            onViewingPermissionChange?.(file.id, "showSubs", isVisible);
        } else {
            return;
        }

        const isTempFile = file.tempId !== undefined;
        const loginKey = loginType === BTLoginTypes.CONTACT ? "showOwner" : "showSubs";
        if (isTempFile) {
            updateViewingPermissions(file.id, loginKey, isVisible);
            return;
        }

        try {
            await setActionBeingPerformed({
                actionBeingPerformed: "updateViewPermissions",
                callback: async () => {
                    await documentHandler.toggleDocViewPermissions(file.id, loginType, isVisible);
                },
            });

            updateViewingPermissions(file.id, loginKey, isVisible);
        } catch (e) {
            showAPIErrorMessage(
                e,
                "There was an error updating permissions for this file, please try again",
            );
        }
    };

    const getLinkedDocumentInstances = useCallback(
        async (docInstanceId: number) => {
            try {
                return await mediaListHandler.getLinkedDocumentInstances(
                    docInstanceId,
                    entity.id === 0,
                );
            } catch (e) {
                showAPIErrorMessage(e);
                return;
            }
        },
        [entity.id, mediaListHandler],
    );

    const handleEditOnline = useCallback(
        async (docInstanceId: number, mediaType: MediaType) => {
            try {
                const response = await mediaListHandler.getLinkedDocumentInstances(
                    docInstanceId,
                    entity.id === 0,
                );
                if (response && response.attachedMedia.length > 0) {
                    // We set callbackNavigates to false because we need to navigate back to the correct route based on the action after replacing the file (two redirects occur based on the action)
                    // We take care of setting the action back to undefined in beforeClose callbacks for respective model
                    await setActionBeingPerformed({
                        actionBeingPerformed: "editOnline",
                        callback: (): Promise<void> => {
                            setCurrentFile({
                                docInstanceId,
                                attachedMedia: response.attachedMedia,
                            } as IFileMetadata);

                            const replaceLink = routes.media.getReplaceLink(
                                docInstanceId,
                                false,
                                mediaType,
                            );
                            history.replace(getAttachmentRoute(replaceLink, docInstanceId));
                            return Promise.resolve();
                        },
                        callbackNavigates: false,
                    });
                } else {
                    await openFileInZoho(docInstanceId);
                }
            } catch (e) {
                showAPIErrorMessage(e);
            }
        },
        [
            entity.id,
            getAttachmentRoute,
            history,
            mediaListHandler,
            openFileInZoho,
            setActionBeingPerformed,
        ],
    );

    const handleDraw = useCallback(
        async (docInstanceId: number, annotationType: AnnotationType, mediaType: MediaType) => {
            if (!userInfo) {
                return;
            }

            if (
                userInfo.loginType === BTLoginTypes.CONTACT ||
                userInfo.loginType === BTLoginTypes.OWNER
            ) {
                return;
            }

            const response = await getLinkedDocumentInstances(docInstanceId);
            if (response && response.attachedMedia.length > 0) {
                // We set callbackNavigates to false because we need to navigate back to the correct route based on the action after replacing the file (two redirects occur based on the action)
                // We take care of setting the action back to undefined in beforeClose callbacks for respective model
                await setActionBeingPerformed({
                    actionBeingPerformed: "draw",
                    callback: (): Promise<void> => {
                        setCurrentFile({
                            docInstanceId,
                            annotationType,
                            attachedMedia: response.attachedMedia,
                            isOnTempFile: annotationType === AnnotationType.TempFile,
                        } as IFileMetadata);

                        const replaceLink = routes.media.getReplaceLink(
                            docInstanceId,
                            false,
                            mediaType,
                        );
                        history.replace(getAttachmentRoute(replaceLink, docInstanceId));
                        return Promise.resolve();
                    },
                    callbackNavigates: false,
                });
            } else {
                setCurrentFile({
                    docInstanceId: docInstanceId,
                    annotationType,
                } as IFileMetadata);
                const drawLink = enableNutrientPhotoDraw
                    ? routes.media.getMediaPropertiesLink(
                          docInstanceId,
                          MediaType.Photo,
                          entity.jobId,
                      )
                    : routes.photo.getPhotoDrawLink(
                          docInstanceId,
                          entity.builderId,
                          userInfo.loginType,
                          entity.id === 0,
                          annotationType === AnnotationType.TempFile,
                      );
                history.push(getAttachmentRoute(drawLink, docInstanceId));
            }
        },
        [
            userInfo,
            getLinkedDocumentInstances,
            setActionBeingPerformed,
            history,
            getAttachmentRoute,
            entity.builderId,
            entity.id,
            entity.jobId,
            enableNutrientPhotoDraw,
        ],
    );

    const handleSetAsMainPhotoAsync = async (mainPhoto: BTFileSystem) => {
        try {
            if (!mainPhoto.isTempFile && mainPhoto.jobsiteId) {
                const request: SetMainPhotoRequest = {
                    associatedId: mainPhoto.entityId,
                    builderId: mainPhoto.builderId,
                    documentInstanceId: mainPhoto.id,
                    documentType: entity.documentInstanceType,
                    jobId: mainPhoto.jobsiteId,
                };

                await documentHandler.setMainPhoto(request);
            }
        } catch (e) {
            showAPIErrorMessage(e);
        }

        const updatedFiles = files.map((f) => ({
            ...f,
            viewingPermissions: {
                ...f.viewingPermissions,
                mainFile: f.id === mainPhoto.id,
            } as BTViewingPermissions,
        }));

        handleFilesChanged(updatedFiles);

        void message.success("Main photo updated");
    };

    const handleCloseLightbox = useCallback(() => setSelectedLightboxFileId(null), []);

    const refreshDocumentList = (documentInstanceId: number) => {
        const updatedFiles = files.filter((file) => file.id !== documentInstanceId);
        handleFilesChanged(updatedFiles);
    };

    const updateDocumentInstance = (
        documentInstanceId: number,
        updatedProperties: Partial<BTFileSystem>,
    ) => {
        const updatedFiles = files.map((file) => {
            if (file.id === documentInstanceId) {
                return {
                    ...file,
                    ...updatedProperties,
                };
            }
            return file;
        });

        handleFilesChanged(updatedFiles);
    };

    const slides = useMemo(
        () =>
            files
                .filter(canViewBTFileSystemInLightbox)
                .map(btFileSystemToSlide)
                .sort(sortMediaByMainFileAndDateAttached),
        [files],
    );
    const index = slides.findIndex((s) => s.metadata?.mediaId === selectedLightboxFileId);
    const open = index >= 0;
    const downloadAllCount = files.filter((f) => f.mediaType !== MediaType.Video).length;
    const canDownloadAll =
        !isOnExternalPage &&
        entity.id !== 0 &&
        downloadAllCount > 1 &&
        isAttachedFilesBulkDownloadSupported(entity.documentInstanceType);
    const hasVideo = downloadAllCount < files.length;
    const isTemplateMode = getJobPickerState().isTemplateMode;
    const canAddFiles = !isReadOnly && entity.attachedFiles.viewingPermissions.canAddFiles;
    const editOnlineHandler =
        hideCreateAndEditDocOnline || mobile.isWebview ? undefined : handleEditOnline;
    const canCreateNewDoc =
        userInfo?.loginType === BTLoginTypes.BUILDER &&
        !isOnExternalPage &&
        !hideCreateAndEditDocOnline;

    const modalConfig: IModalConfiguration = {
        parentRoute: baseRoute,
        beforeClose: () => history.replace(baseRoute),
    };

    const mediaReplaceModalConfig: IModalConfiguration = {
        parentRoute: baseRoute,
        beforeClose: () => void handleReplaceBeforeClose(),
    };

    const drawModalConfig: IModalConfiguration = {
        parentRoute: baseRoute,
        beforeClose: () => void handleCloseDrawModal(),
    };

    const handleSelectedFileChange = useCallback((fileId: number, isSelected: boolean) => {
        setSelectedFileIds((prevSelectedIds) => {
            if (isSelected && !prevSelectedIds.includes(fileId)) {
                return [...prevSelectedIds, fileId];
            } else if (!isSelected && prevSelectedIds.includes(fileId)) {
                return prevSelectedIds.filter((id) => id !== fileId);
            }
            return prevSelectedIds;
        });
    }, []);

    // Check if any selected files are non-deletable (e.g., base file for OCR)
    const hasNonDeletableFileSelected = selectedFileIds.some((fileId) =>
        nonDeletableFileIds?.includes(fileId),
    );

    // True when every file is non-deletable — show a disabled button with tooltip
    const hasOnlyNonDeletableFiles =
        !!nonDeletableTooltip &&
        files.length > 0 &&
        files.every((file) => nonDeletableFileIds?.includes(file.id));

    // Handler for bulk deletion of selected files
    const handleDeleteSelectedFiles = async () => {
        if (!filesAreSelected) {
            return;
        }

        const selectedFiles = files.filter((file) => selectedFileIds.includes(file.id));
        const isTempFile = selectedFiles[0].tempId !== undefined || isOnNewEntity;

        // If only 1 file is selected, use the same confirmation dialog as the individual file delete
        if (selectedFiles.length === 1) {
            await handleFileDelete(selectedFiles[0]);
            return;
        }

        const title = isTempFile
            ? `Remove attachments from this ${getDocumentInstanceTypeString(
                  entity.documentInstanceType,
              )}`
            : "Move attachments to trash";
        const content = isTempFile
            ? `Are you sure you want to remove these attachments from this ${getDocumentInstanceTypeString(
                  entity.documentInstanceType,
              )}?`
            : `These files will only be removed from this ${getDocumentInstanceTypeString(
                  entity.documentInstanceType,
              )}. If they are attached elsewhere, they will remain attached there.`;
        const okText = isTempFile ? "Remove" : "Move to trash";

        const deleteConfirmAsync = await btDeleteConfirmAsync({
            title,
            content,
            okText,
            okButtonProps: { "data-testid": "confirmRemoveSelectedAttachmentsButton" },
            cancelText: "Cancel",
            cancelButtonProps: { "data-testid": "cancelRemoveSelectedAttachmentsButton" },
        });

        if (deleteConfirmAsync === "cancel") {
            return;
        }

        try {
            if (!isTempFile) {
                await setActionBeingPerformed({
                    actionBeingPerformed: "delete",
                    callback: async () => {
                        const fileIds = selectedFiles.map((file) => file.id);
                        await documentHandler.delete(
                            entity.documentInstanceType,
                            fileIds,
                            entity.builderId,
                            entity.id,
                            entity.jobId,
                        );
                    },
                });
            }

            // Update the file list to remove the deleted files
            const remainingFiles = files.filter((file) => !selectedFileIds.includes(file.id));
            handleFilesChanged(remainingFiles);

            // Clear selected files
            setSelectedFileIds([]);

            await message.success(
                isTempFile ? "Files deleted successfully" : "Files moved to trash successfully",
            );
        } catch (e) {
            showAPIErrorMessage(
                e,
                "There was an error deleting one or more files, please try again",
            );
        }
    };

    return (
        <div className={cn(!removeVerticalPadding && "padding-vertical-sm")}>
            <div className="flex flex-row attachment-container gap-2 justify-between items-center">
                <div className="flex flex-row gap-2 items-center grow">
                    {showTitle && (
                        <div
                            id="attachmentsActionBarLeft"
                            className="flex flex-col flex align-items-baseline"
                        >
                            <BTText size="distinct-md" className="padding-right-sm">
                                Attachments
                            </BTText>
                        </div>
                    )}
                    <div className="flex flex-col grow">
                        <BdsButtonGroup
                            key={buttonGroupKey}
                            id="attachmentActionsButtonGroup"
                            testid="attachmentActionsButtonGroup"
                            menuTestid="attachmentActionsButtonGroupMenu"
                            rollupTestid="attachmentActionsButtonGroupRollup"
                            grow
                            reverse
                        >
                            <>
                                {(filesAreSelected || hasOnlyNonDeletableFiles) && (
                                    <BdsRollupButton
                                        id="deleteSelectedFiles"
                                        testid="deleteSelectedFiles"
                                        displayType="danger"
                                        onClick={handleDeleteSelectedFiles}
                                        loading={actionBeingPerformed === "delete"}
                                        disabled={
                                            hasNonDeletableFileSelected || hasOnlyNonDeletableFiles
                                        }
                                        text={isOnNewEntity ? "Remove selected" : "Move to trash"}
                                        value="deleteSelectedFiles"
                                        tooltip={
                                            (hasNonDeletableFileSelected ||
                                                hasOnlyNonDeletableFiles) &&
                                            nonDeletableTooltip ? (
                                                <BTText testid="nonDeletableTooltip">
                                                    {nonDeletableTooltip}
                                                </BTText>
                                            ) : undefined
                                        }
                                    />
                                )}
                                {canDownloadAll && !filesAreSelected && (
                                    <BdsRollupButton
                                        id="downloadAll"
                                        testid="downloadAll"
                                        text={`Download all (${downloadAllCount})`}
                                        value="downloadAll"
                                        displayType="secondary"
                                        onClick={handleBulkDownload}
                                        loading={actionBeingPerformed === "download"}
                                        tooltip={
                                            <BTText testid="downloadAllPopover">
                                                Videos cannot be downloaded at this time.
                                            </BTText>
                                        }
                                        suppressTooltip={
                                            actionBeingPerformed !== undefined || !hasVideo
                                        }
                                        disabled={actionBeingPerformed !== undefined}
                                    />
                                )}
                                {canAddFiles && !filesAreSelected && (
                                    <>
                                        {canCreateNewDoc && !mobile.isWebview && (
                                            <BdsRollupButton
                                                id="createNewDocButton"
                                                testid="createNewDoc"
                                                text="Create new doc"
                                                value="createNewDoc"
                                                onClick={handleOpenCreateNewDocModal}
                                            />
                                        )}
                                        {useRNFilePicker && (
                                            <BdsRollupButton
                                                id="browseBTFilesButton"
                                                testid="browseBTFiles"
                                                text="Browse Buildertrend"
                                                value="browseBTFiles"
                                                onClick={handleClickBrowseBTFiles}
                                            />
                                        )}
                                        <BdsRollupButton
                                            id="addButton"
                                            testid="addNewFile"
                                            text="Add"
                                            value="add"
                                            onClick={handleClickAdd}
                                            minStage={1}
                                        />
                                    </>
                                )}
                            </>
                        </BdsButtonGroup>
                    </div>
                    {filesAreSelected && (
                        <div className="flex flex-col items-start attachment-container-selected-count">
                            <BTText
                                className="selected-count"
                                text={`${selectedFileIds.length} selected`}
                            />
                        </div>
                    )}
                </div>
                {files.length > 0 && !hideTableView && (
                    <div className="flex flex-col attachment-container-file-view-toggle">
                        <div id="attachmentsActionBarRight" onClick={(e) => e.stopPropagation()}>
                            <FileViewToggle value={view} onChange={handleChangeView} />
                        </div>
                    </div>
                )}
            </div>

            {files.length === 0 && <></>}
            {(view === "Tile" || hideTableView) && files.length > 0 && (
                <MediaGrid
                    files={files}
                    isReadOnly={isReadOnly}
                    isShowingMore={isShowingMore}
                    onShowMore={setIsShowingMore}
                    onOpenLightbox={setSelectedLightboxFileId}
                    onFileClick={onFileClick}
                    onFileDelete={handleFileDelete}
                    onVideoDownload={handleVideoDownload}
                    onViewPermissionsUpdate={handleViewPermissionsUpdate}
                    onFileDraw={handleDraw}
                    onEditOnline={editOnlineHandler}
                    fileViewOptions={entity.attachedFiles.newFileOptions}
                    entityId={entity.id}
                    entityName={entity.name}
                    isOnExternalPage={isOnExternalPage}
                    onSetMainPhoto={handleSetAsMainPhotoAsync}
                    selectedFileIds={selectedFileIds}
                    onSelectedFileChange={handleSelectedFileChange}
                    nonDeletableFileIds={nonDeletableFileIds}
                    {...(onFileOrderChanged && !isReadOnly
                        ? { onReorderFiles: handleReorderFiles }
                        : {})}
                />
            )}
            {view === "Table" && !hideTableView && files.length > 0 && (
                <MediaTable
                    files={files}
                    isReadOnly={isReadOnly}
                    isShowingMore={isShowingMore}
                    fileViewOptions={entity.attachedFiles.newFileOptions}
                    onShowMore={setIsShowingMore}
                    onOpenLightbox={setSelectedLightboxFileId}
                    onFileClick={onFileClick}
                    onFileDelete={handleFileDelete}
                    onVideoDownload={handleVideoDownload}
                    onViewPermissionsUpdate={handleViewPermissionsUpdate}
                    onFileDraw={handleDraw}
                    onEditOnline={editOnlineHandler}
                    hasStickyHeader={hasStickyTableHeader}
                    entityId={entity.id}
                    entityName={entity.name}
                    isOnExternalPage={isOnExternalPage}
                    onSetMainPhoto={handleSetAsMainPhotoAsync}
                    selectedFileIds={selectedFileIds}
                    onSelectedFileChange={handleSelectedFileChange}
                    nonDeletableFileIds={nonDeletableFileIds}
                    scroll={{ x: true }}
                    highlightedFileId={highlightedFileId}
                    {...(onSortChange ? { onSortChange } : {})}
                    {...(onFileOrderChanged && !isReadOnly
                        ? { onReorderFiles: handleReorderFiles }
                        : {})}
                    {...(enableRename && {
                        renamingId,
                        isSavingRename: actionBeingPerformed === "rename",
                        onClickRename: handleStartRename,
                        onSubmitRename: handleSubmitRename,
                        onCancelRename: handleCancelRename,
                    })}
                />
            )}
            {showUploadFileModal && (
                <BTFileUpload
                    entity={entity}
                    jobId={entity.jobId}
                    mediaType={MediaType.Unavailable}
                    uploadMode={BTFileUploadMode.Attachments}
                    isTemplateMode={isTemplateMode}
                    fileList={[]}
                    modalConfig={{ parentRoute: "", beforeClose: handleCloseUploadModal }}
                    onFilesConfirmed={handleFilesAdded}
                    externalServiceInfo={externalServiceInfo}
                    isExternal={isOnExternalPage}
                    nextViewingOrder={nextViewingOrder}
                />
            )}
            {showBrowseBTFilesModal && (
                <BrowseBTFiles
                    jobId={entity.jobId}
                    leadId={entity.leadId}
                    allowedFileTypes={entity.attachedFiles.viewingPermissions.allAllowedExtensions}
                    modalConfig={{
                        parentRoute: "",
                        beforeClose: handleCloseBrowseBTFiles,
                    }}
                    onCancel={handleCloseBrowseBTFiles}
                    onSubmit={handleBrowseBTFilesSelected}
                />
            )}
            <BTLightbox
                slides={slides}
                open={open}
                index={index}
                onClose={handleCloseLightbox}
                onSlideChange={handleSlideChange}
            />
            {canAddFiles && canCreateNewDoc && showCreateNewDocModal && (
                <DocumentCreate
                    builderId={entity.builderId}
                    jobId={entity.jobId!}
                    modalConfig={{
                        parentRoute: "",
                        beforeClose: handleCloseCreateNewDocModal,
                    }}
                    attachmentMode={{
                        entity: entity,
                        onDocumentCreated: (file: BTFileSystem) => {
                            handleFilesAdded([file]);
                            handleCloseCreateNewDocModal();
                            void openFileInZoho(file.id);
                        },
                        onCancel: handleCloseCreateNewDocModal,
                    }}
                />
            )}
            {showViewAllModal && (
                <ViewAllAttachments
                    entity={entity}
                    jobId={entity.jobId}
                    files={files}
                    modalConfig={{ parentRoute: "", beforeClose: handleCloseViewAllModal }}
                    isReadonly={isReadOnly}
                    onAddClicked={handleOpenViewAllFromAdd}
                    onFileDraw={handleDraw}
                    onEditOnline={editOnlineHandler}
                    onFileDelete={handleFilesChanged}
                    isOnExternalPage={isOnExternalPage}
                />
            )}
            <RouteMediaPropertiesFromAttachedFiles
                modalConfig={modalConfig}
                entityName={entity.name}
                handleUpdateDocumentInstance={updateDocumentInstance}
                onDocumentDeleted={refreshDocumentList}
            />
            <RouteRelative path={attachmentRoute}>
                <RouteMediaReplace
                    attachedMedia={currentFile?.attachedMedia}
                    modalConfig={mediaReplaceModalConfig}
                    onReplace={handleReplace}
                    onCancel={handleReplaceBeforeClose}
                />
                <RouteZoho
                    zohoSessionInfo={zohoSessionInfo}
                    docBrowserInfoCallBack={getZohoDocBrowserInfo}
                    beforeClose={handleZohoBeforeClose}
                />
                <RoutePhotoDraw modalConfig={drawModalConfig} />
            </RouteRelative>
        </div>
    );
});
